// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocalization } from '../../../../../config/localization';
import { getSafeExternalUrl } from '../../../../../utils/safeExternalUrl';
import { getWorkflowUrlRole } from '../../../../../utils/workflowUrlRoles';
import CopyValueButton from '../../../../ui/CopyValueButton';
import Icon, { ICONS } from '../../../../ui/icons';
import ConfirmationModal from '../../../../ui/overlays/modals/ConfirmationModal';
import { type PromptOption } from '../../../../ui/SearchablePromptSelect';
import WorkflowUrlEditModal from './WorkflowUrlEditModal';
import type { ApiEndpointsSectionProps } from './index';

interface WorkflowUrl {
  id: number;
  workflow_id: number;
  url_index: number;
  url: string;
  api_key: string | null;
  description: string;
  prompt_id?: number | null;
  created_at: string;
  updated_at?: string;
  dify_workflow_url?: string | null;
  extra_params?: string | null;
}

interface WorkflowUrlFormData {
  id?: number;
  url_index: number;
  url: string;
  api_key: string;
  description: string;
  prompt_id?: number | null;
  dify_workflow_url?: string;
  extra_params?: Record<string, string>;
}

export default function ApiEndpointsSection({
  workflowId,
  language,
  formData
}: ApiEndpointsSectionProps) {
  const { getToken } = useAuth();
  const t = useLocalization(language);

  const [urls, setUrls] = useState<WorkflowUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingUrl, setEditingUrl] = useState<WorkflowUrlFormData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showApiKeys, setShowApiKeys] = useState<Record<number | string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showExtractConfirm, setShowExtractConfirm] = useState<WorkflowUrl | null>(null);
  const [extracting, setExtracting] = useState(false);

  // Workflow prompts state
  const [workflowPrompts, setWorkflowPrompts] = useState<PromptOption[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);

  useEffect(() => {
    loadWorkflowUrls();
    loadWorkflowPrompts();
  }, [workflowId]);

  const loadWorkflowUrls = async () => {
    try {
      setLoading(true);
      const token = await getToken({ template: 'withmail' });

      const response = await fetch(`/api/admin/workflows/${workflowId}/urls`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUrls(data.urls || []);
      } else {
        setError('Failed to load workflow URLs');
      }
    } catch (error) {
      console.error('Error loading workflow URLs:', error);
      setError('Failed to load workflow URLs');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowPrompts = async () => {
    try {
      setPromptsLoading(true);
      const token = await getToken({ template: 'withmail' });
      const response = await fetch('/api/admin/prompts/workflow', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWorkflowPrompts(data.prompts || []);
      } else {
        setWorkflowPrompts([]);
      }
    } catch (error) {
      console.error('Error loading workflow prompts:', error);
      setWorkflowPrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  };

  const handleSaveUrl = async () => {
    if (!editingUrl) return;

    try {
      setSaving(true);
      const token = await getToken({ template: 'withmail' });

      if (editingUrl.id) {
        // Update existing URL
        const updateData = {
          url: editingUrl.url,
          api_key: editingUrl.api_key || null,
          description: editingUrl.description,
          prompt_id: editingUrl.prompt_id ? Number(editingUrl.prompt_id) : null,
          dify_workflow_url: editingUrl.dify_workflow_url || null,
          extra_params: editingUrl.extra_params && Object.keys(editingUrl.extra_params).length > 0
            ? JSON.stringify(editingUrl.extra_params)
            : null
        };

        const response = await fetch(`/api/admin/workflows/${workflowId}/urls/${editingUrl.url_index}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        if (!response.ok) {
          throw new Error('Failed to update URL');
        }
      } else {
        // Create new URL
        const response = await fetch(`/api/admin/workflows/${workflowId}/urls`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            url_index: editingUrl.url_index,
            url: editingUrl.url,
            api_key: editingUrl.api_key || null,
            description: editingUrl.description,
            prompt_id: editingUrl.prompt_id ? Number(editingUrl.prompt_id) : null,
            dify_workflow_url: editingUrl.dify_workflow_url || null,
            extra_params: editingUrl.extra_params && Object.keys(editingUrl.extra_params).length > 0
              ? JSON.stringify(editingUrl.extra_params)
              : null
          })
        });

        if (!response.ok) {
          throw new Error('Failed to create URL');
        }
      }

      setEditingUrl(null);
      await loadWorkflowUrls();
    } catch (error) {
      console.error('Error saving URL:', error);
      setError('Failed to save URL');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUrl = async (urlIndex: number) => {
    try {
      setSaving(true);
      const token = await getToken({ template: 'withmail' });

      const response = await fetch(`/api/admin/workflows/${workflowId}/urls/${urlIndex}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete URL');
      }

      setShowDeleteConfirm(null);
      await loadWorkflowUrls();
    } catch (error) {
      console.error('Error deleting URL:', error);
      setError('Failed to delete URL');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUrl = (url: WorkflowUrl) => {
    let parsedExtraParams: Record<string, string> = {};
    if (url.extra_params) {
      try {
        parsedExtraParams = JSON.parse(url.extra_params);
      } catch (e) {
        console.error('Failed to parse extra_params:', e);
      }
    }

    setEditingUrl({
      id: url.id,
      url_index: url.url_index,
      url: url.url,
      api_key: url.api_key || '',
      description: url.description,
      prompt_id: url.prompt_id || null,
      dify_workflow_url: url.dify_workflow_url || '',
      extra_params: parsedExtraParams
    });
  };

  const handleAddUrl = () => {
    const usedIndexes = urls.map(url => url.url_index);

    let nextIndex = 0;
    while (usedIndexes.includes(nextIndex) && nextIndex <= 5) {
      nextIndex++;
    }

    if (nextIndex > 5) {
      setError(t.adminWorkflows?.urlManager?.maxUrlsReached || 'Maximum number of URLs reached (6 total)');
      return;
    }

    const isMainUrl = nextIndex === 0;
    setEditingUrl({
      url_index: nextIndex,
      url: 'https://api.loremax.ai/v1/workflows/run',
      api_key: '',
      description: isMainUrl ? 'Main URL' : `Additional URL ${nextIndex}`,
      prompt_id: null,
      dify_workflow_url: '',
      extra_params: {}
    });
  };

  const toggleApiKeyVisibility = (urlId: number | string) => {
    setShowApiKeys(prev => ({
      ...prev,
      [urlId]: !prev[urlId]
    }));
  };

  const maskApiKey = (apiKey: string | null) => {
    if (!apiKey) return t.adminWorkflows?.urlManager?.noApiKey || 'No API key';
    if (apiKey.length <= 8) return apiKey;
    return apiKey.substring(0, 8) + '...';
  };

  const handleExtractToTextWorkflow = async (sourceUrl: WorkflowUrl) => {
    try {
      setExtracting(true);
      const token = await getToken({ template: 'withmail' });

      const parentTitle = formData?.title || 'Workflow';
      const suffix = t.adminWorkflows?.urlManager?.extractWorkflowSuffix || 'Prompt Enhance';
      const newTitle = `${parentTitle} - ${suffix}`;
      const visualId = `text-${parentTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-enhance`;

      // Step 1: Create new Text workflow
      const createResponse = await fetch('/api/admin/workflows', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTitle,
          visual_id: visualId,
          category: 'Text',
          description_translations: { ru: newTitle, en: newTitle },
          pricing_formula: '100',
          is_available_for_all: false
        })
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create text workflow');
      }

      const createData = await createResponse.json();
      const newWorkflowId = createData.data;

      // Step 2: Copy URL config from source as main URL (url_index=0)
      const urlResponse = await fetch(`/api/admin/workflows/${newWorkflowId}/urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url_index: 0,
          url: sourceUrl.url,
          api_key: sourceUrl.api_key || null,
          description: t.adminWorkflows?.urlManager?.extractMainUrlDescription || 'Main URL',
          prompt_id: sourceUrl.prompt_id || null,
          dify_workflow_url: sourceUrl.dify_workflow_url || null,
          extra_params: sourceUrl.extra_params || null
        })
      });

      if (!urlResponse.ok) {
        throw new Error('Failed to create URL for text workflow');
      }

      setShowExtractConfirm(null);
      setError(null);
      setSuccessMessage(t.adminWorkflows?.urlManager?.extractSuccess || 'Text workflow created successfully');

    } catch (err) {
      console.error('Error extracting to text workflow:', err);
      setError(t.adminWorkflows?.urlManager?.extractError || 'Failed to create text workflow');
    } finally {
      setExtracting(false);
    }
  };

  const getUrlTypeLabel = (index: number) => {
    switch (index) {
      case 0: return 'Main';
      case 1: return 'Additional 1';
      case 2: return 'Additional 2';
      case 3: return 'Additional 3';
      case 4: return 'Additional 4';
      case 5: return 'Additional 5';
      default: return `URL ${index}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.LINK} size={20} className="text-blue-400" />
          {t.admin?.apiEndpoints || 'API Endpoints'}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {t.admin?.apiEndpointsDesc || 'Configure API URLs for this workflow. Main URL is required, additional URLs are optional.'}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`px-3 py-1.5 rounded-lg border ${
            urls.length > 0
              ? 'bg-green-900/30 border-green-500/40'
              : 'bg-red-900/30 border-red-500/40'
          }`}>
            <span className={`text-sm ${urls.length > 0 ? 'text-green-300' : 'text-red-300'}`}>
              {urls.length > 0
                ? (t.admin?.mainUrlConfigured || 'Main URL configured')
                : (t.admin?.mainUrlMissing || 'Main URL missing')
              }
            </span>
          </div>
          <div className="px-3 py-1.5 bg-gray-800/50 border border-gray-600/40 rounded-lg">
            <span className="text-sm text-gray-300">
              {t.admin?.totalEndpoints || 'Total'}: <span className="font-bold text-white">{urls.length}/6</span>
            </span>
          </div>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-xs text-red-300 hover:text-red-200 mt-1"
          >
            {t.common?.dismiss || 'Dismiss'}
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
          <p className="text-green-400 text-sm">{successMessage}</p>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-xs text-green-300 hover:text-green-200 mt-1"
          >
            {t.common?.dismiss || 'Dismiss'}
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* URLs List */}
          {urls.map((url) => (
            <div key={url.id} className="bg-gray-800/50 border border-gray-600/50 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-sm font-medium px-2 py-1 rounded ${
                      url.url_index === 0
                        ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30'
                        : 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                    }`}>
                      {getUrlTypeLabel(url.url_index)}
                    </span>
                    {url.url_index === 0 && (
                      <span className="text-xs text-orange-400">
                        ({t.adminWorkflows?.urlManager?.required || 'Required'})
                      </span>
                    )}
                    {getWorkflowUrlRole(url.url_index) && (
                      <span className="rounded border border-cyan-500/30 bg-cyan-900/20 px-2 py-1 text-xs font-medium text-cyan-300">
                        {getWorkflowUrlRole(url.url_index) === 'translate'
                          ? (t.adminWorkflows?.urlManager?.roleTranslate || '→ Translate')
                          : (t.adminWorkflows?.urlManager?.rolePromptEnhance || '→ Prompt Enhance')}
                      </span>
                    )}
                  </div>

                  <p className="text-white font-medium mb-1">{url.description}</p>
                  <div className="space-y-1 mb-2">
                    <div className="flex items-start gap-1 text-gray-300 text-sm font-mono">
                      <p className="min-w-0 break-all">
                        <span className="text-gray-400 text-xs">API URL:</span> {url.url}
                      </p>
                      <CopyValueButton
                        value={url.url}
                        title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                        copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                        className="shrink-0"
                      />
                    </div>
                    {url.dify_workflow_url && (
                      <div className="flex items-start gap-1 text-gray-300 text-sm font-mono">
                        <p className="min-w-0 break-all">
                          <span className="text-gray-400 text-xs">Dify URL:</span> {url.dify_workflow_url}
                        </p>
                        <CopyValueButton
                          value={url.dify_workflow_url}
                          title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                          copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                          className="shrink-0"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {t.adminWorkflows?.urlManager?.apiKey || 'API Key'}:
                    </span>
                    <span className="text-xs text-gray-300 font-mono">
                      {showApiKeys[url.id]
                        ? (url.api_key || (t.adminWorkflows?.urlManager?.noApiKey || 'No API key'))
                        : maskApiKey(url.api_key)
                      }
                    </span>
                    {url.api_key && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleApiKeyVisibility(url.id);
                          }}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <Icon
                            name={showApiKeys[url.id] ? ICONS.EYE_OFF : ICONS.EYE}
                            size={14}
                          />
                        </button>
                        <CopyValueButton
                          value={url.api_key}
                          title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                          copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                        />
                      </>
                    )}
                  </div>

                  {/* Extra Parameters */}
                  {url.extra_params && (() => {
                    try {
                      const params = JSON.parse(url.extra_params);
                      const keys = Object.keys(params);
                      if (keys.length > 0) {
                        return (
                          <div className="mt-2">
                            <span className="text-xs text-gray-400">Extra Parameters:</span>
                            <div className="mt-1 space-y-1">
                              {keys.map(key => (
                                <div key={key} className="flex items-center gap-1 text-xs">
                                  <span className="text-gray-300 font-mono">{key}:</span>{' '}
                                  <span className="min-w-0 break-all text-gray-400">{params[key]}</span>
                                  <CopyValueButton
                                    value={String(params[key])}
                                    title={t.adminWorkflows?.urlManager?.copy || 'Copy'}
                                    copiedTitle={t.adminWorkflows?.urlManager?.copied || 'Copied'}
                                    className="shrink-0"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                  })()}

                  {/* Workflow Prompt */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-gray-400">Workflow Prompt:</span>
                    {url.prompt_id ? (
                      (() => {
                        const selectedPrompt = workflowPrompts.find(p => p.id === String(url.prompt_id));
                        return selectedPrompt ? (
                          <span className="text-xs text-green-400 font-medium">
                            {selectedPrompt.title}
                          </span>
                        ) : (
                          <span className="text-xs text-red-400">
                            Prompt not found
                          </span>
                        );
                      })()
                    ) : (
                      <span className="text-xs text-gray-500 italic">
                        No prompt selected
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {getSafeExternalUrl(url.dify_workflow_url) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(getSafeExternalUrl(url.dify_workflow_url), '_blank', 'noopener,noreferrer');
                      }}
                      className="p-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                      title={t.adminWorkflows?.urlManager?.openDifyUrl || 'Open Dify URL'}
                    >
                      <Icon name={ICONS.EXTERNAL_LINK} size={16} />
                    </button>
                  )}
                  {url.url_index === 2 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowExtractConfirm(url);
                      }}
                      className="p-2 bg-amber-600 hover:bg-amber-700 text-white rounded transition-colors"
                      title={t.adminWorkflows?.urlManager?.extractToTextWorkflow || 'Extract to Text Workflow'}
                    >
                      <Icon name={ICONS.TEXT} size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditUrl(url);
                    }}
                    className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    title={t.adminWorkflows?.urlManager?.editUrl || 'Edit'}
                  >
                    <Icon name={ICONS.EDIT} size={16} />
                  </button>
                  {url.url_index !== 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDeleteConfirm(url.url_index);
                      }}
                      className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                      title={t.adminWorkflows?.urlManager?.deleteUrl || 'Delete'}
                    >
                      <Icon name={ICONS.TRASH} size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {urls.length === 0 && (
            <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700/50">
              <Icon name={ICONS.LINK} size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400 mb-2">
                {t.admin?.noApiEndpoints || 'No API endpoints configured'}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {t.admin?.noApiEndpointsDesc || 'Add at least one main URL to enable this workflow.'}
              </p>
            </div>
          )}

          {/* Add URL Button */}
          {urls.length < 6 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddUrl();
              }}
              className="w-full py-3 border-2 border-dashed border-gray-600 rounded-xl text-gray-400 hover:border-blue-500/50 hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
            >
              <Icon name={ICONS.PLUS} size={20} />
              {urls.length === 0
                ? (t.adminWorkflows?.urlManager?.addMainUrl || 'Add Main URL')
                : (t.adminWorkflows?.urlManager?.addUrl || 'Add Additional URL')
              }
            </button>
          )}
        </div>
      )}

      {/* Edit URL Modal */}
      {editingUrl && (
        <WorkflowUrlEditModal
          value={editingUrl}
          onChange={setEditingUrl}
          onSave={handleSaveUrl}
          onCancel={() => setEditingUrl(null)}
          prompts={workflowPrompts}
          promptsLoading={promptsLoading}
          saving={saving}
          language={language}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm !== null && (
        <ConfirmationModal
          isOpen={true}
          title={t.adminWorkflows?.urlManager?.deleteUrl || 'Delete URL'}
          message={(t.adminWorkflows?.urlManager?.confirmDelete || 'Are you sure you want to delete {urlType}? This action cannot be undone.').replace('{urlType}', getUrlTypeLabel(showDeleteConfirm))}
          confirmText={t.adminWorkflows?.urlManager?.deleteUrl || 'Delete'}
          cancelText={t.adminWorkflows?.urlManager?.cancel || 'Cancel'}
          onConfirm={() => handleDeleteUrl(showDeleteConfirm)}
          onCancel={() => setShowDeleteConfirm(null)}
          variant="danger"
        />
      )}

      {/* Extract to Text Workflow Confirmation Modal */}
      {showExtractConfirm !== null && (
        <ConfirmationModal
          isOpen={true}
          title={t.adminWorkflows?.urlManager?.extractConfirmTitle || 'Create Text Workflow'}
          message={t.adminWorkflows?.urlManager?.extractConfirmMessage || 'Create a new Text workflow using this enhance URL as main endpoint?'}
          confirmText={extracting
            ? (t.adminWorkflows?.urlManager?.extracting || 'Creating...')
            : (t.adminWorkflows?.urlManager?.extractToTextWorkflow || 'Extract to Text Workflow')
          }
          cancelText={t.adminWorkflows?.urlManager?.cancel || 'Cancel'}
          onConfirm={() => handleExtractToTextWorkflow(showExtractConfirm)}
          onCancel={() => setShowExtractConfirm(null)}
          confirmDisabled={extracting}
          variant="info"
        />
      )}
    </div>
  );
}
